import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { UitkCardContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkPriceLockup } from 'uitk-react-price-lockup';
import { UitkText } from 'uitk-react-text';
import { ImageBadges } from '../..';
import { getPriceDetails } from '../../../../../../business';
import { ActivityCancellationType, ActivityDestinationViewSearch } from '../../../../../../common/__generated__/api';
import { useStores } from '../../../../../../utils/hooks';
import { MessageAnchor } from '../../../../../common/message-anchor';
import { ActivityFeature } from '../components/activity-feature';
import { ActivitySpannedTileCardTitle } from './activity-spanned-tile-card-title';

export interface ActivitySpannedTileCardContentProps {
  activity: ActivityDestinationViewSearch.ActivityInlineFragment;
  isVerticalLayout?: boolean;
  isInCarousel?: boolean;
  titleLines?: number;
  className?: string;
  onClick?: (activity: ActivityDestinationViewSearch.ActivityInlineFragment, index: number) => void;
}

export const ActivitySpannedTileCardContent: React.FC<ActivitySpannedTileCardContentProps> = ({
  activity,
  isVerticalLayout,
  isInCarousel,
  titleLines
}) => {
  const secondaryBadge = activity.badges?.secondary;
  const { formatText } = useLocalization();
  const { routeStore, analytics } = useStores();
  const {
    strikeOutFormattedPrice,
    leadFormattedPrice,
    leadFormattedPoints,
    leadPriceLabel,
    priceAccessibilityText
  } = getPriceDetails(activity);

  const hotelPickup = activity.features.hotelPickup;

  return (
    <UitkLayoutGridItem colSpan={{ medium: isVerticalLayout ? 1 : 2 }}>
      <UitkCardContentSection>
        <UitkLayoutFlex
          tag="section"
          direction="column"
          className="uitk-card-text-segment full-width max-height"
          alignContent="space-between"
        >
          {isVerticalLayout ? null : (
            <UitkLayoutFlexItem>
              <ActivitySpannedTileCardTitle
                activity={activity}
                isInCarousel={isInCarousel}
                isVerticalLayout={isVerticalLayout}
                titleLines={titleLines}
              />
            </UitkLayoutFlexItem>
          )}
          <UitkLayoutFlexItem grow={1}>
            <UitkLayoutFlex
              wrap="nowrap"
              tag="section"
              direction="row"
              data-testid="activity-spanned-tile-card-content"
              justifyContent="space-between"
              className="max-height"
            >
              <UitkLayoutFlexItem>
                <UitkLayoutFlex wrap="nowrap" tag="section" direction="column" justifyContent="space-between">
                  <UitkLayoutFlexItem>
                    <UitkLayoutFlex wrap="nowrap" tag="section" direction="column" justifyContent="start">
                      {activity.duration.formatted && (
                        <UitkLayoutFlexItem className="uitk-type-200 all-b-margin-one">
                          <ActivityFeature
                            label={activity.duration.formatted}
                            a11yLabel={formatText('lx.search.activity.duration', activity.duration.formatted)}
                            icon={activity.duration.icon}
                            data-testid="activity-duration--feature"
                          />
                        </UitkLayoutFlexItem>
                      )}
                      {activity.reviewSummary?.reviewRatingMessage && (
                        <UitkLayoutFlexItem>
                          <UitkLayoutFlex alignItems="center" data-testid="activity-review-rating">
                            <UitkText size={400} weight="bold" theme="emphasis" className="all-r-margin-one">
                              {activity.reviewSummary.reviewRatingMessage}
                            </UitkText>
                            <UitkText size={300} theme="default">
                              {activity.reviewSummary.reviewCountMessage}
                            </UitkText>
                            <Experiment name="LX_3P_Reviews">
                              <ExperimentControl />
                              <ExperimentVariant bucket={1}>
                                {activity.reviewSummary.verifiedReviewsMessage && (
                                  <MessageAnchor
                                    dialogId={`review-legal-disclaimer-dialog-${activity.id}`}
                                    text={activity.reviewSummary.verifiedReviewsMessage.text}
                                    id={`review-info-${activity.id}`}
                                    icon={activity.reviewSummary.verifiedReviewsMessage.icon}
                                    clickAction={activity.reviewSummary.verifiedReviewsMessage.clickAction}
                                    anchorClasses="uitk-type-300 type-gray"
                                    routeStore={routeStore}
                                    analytics={analytics}
                                    isNestedPopup={true}
                                    showText={false}
                                  />
                                )}
                              </ExperimentVariant>
                            </Experiment>
                          </UitkLayoutFlex>
                        </UitkLayoutFlexItem>
                      )}
                      {activity.features.travelDuration?.formatted && (
                        <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-two">
                          <ActivityFeature
                            label={activity.features.travelDuration.formatted}
                            data-testid="activity-travel-duration--feature"
                          />
                        </UitkLayoutFlexItem>
                      )}
                      {activity.features.travelDistance?.label && (
                        <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-two">
                          <ActivityFeature
                            label={activity.features.travelDistance.label}
                            data-testid="activity-travel-distance--feature"
                          />
                        </UitkLayoutFlexItem>
                      )}
                      {hotelPickup && (
                        <UitkLayoutFlexItem className="uitk-type-200 all-t-margin-two">
                          <ActivityFeature
                            label={hotelPickup.label}
                            icon={hotelPickup.icon_temp}
                            data-testid="activity-hotel-pickup--feature"
                          />
                        </UitkLayoutFlexItem>
                      )}
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>
                  {(activity.features.freeCancellation || activity.features.loyaltyEarnAwards) && (
                    <UitkLayoutFlexItem>
                      <UitkLayoutFlex
                        wrap="nowrap"
                        tag="section"
                        direction="column"
                        justifyContent="end"
                        className="all-t-margin-three"
                      >
                        {activity.features.freeCancellation?.type === ActivityCancellationType.FREE_CANCELLATION && (
                          <UitkLayoutFlexItem>
                            <UitkText size={200} theme="positive" data-testid="activity-free-cancellation">
                              {activity.features.freeCancellation.formatted}
                            </UitkText>
                          </UitkLayoutFlexItem>
                        )}

                        {activity.features.loyaltyEarnAwards && (
                          <UitkLayoutFlexItem className="uitk-type-300 color-accent-3 all-t-margin-one">
                            <ActivityFeature
                              label={activity.features.loyaltyEarnAwards.label}
                              mark={activity.features.loyaltyEarnAwards.mark}
                              data-testid="loyalty-earn-award--feature"
                            />
                          </UitkLayoutFlexItem>
                        )}
                      </UitkLayoutFlex>
                    </UitkLayoutFlexItem>
                  )}
                </UitkLayoutFlex>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem maxWidth="half_width">
                <UitkLayoutFlex wrap="nowrap" tag="section" direction="column" justifyContent="end">
                  {!isVerticalLayout && secondaryBadge && (
                    <UitkLayoutFlexItem alignSelf="end">
                      <div className="all-b-margin-one" data-testid="activity-badge">
                        <ImageBadges badges={{ secondary: secondaryBadge }} isVerticalLayout={false} />
                      </div>
                    </UitkLayoutFlexItem>
                  )}
                  <UitkLayoutFlexItem>
                    <UitkPriceLockup
                      lockupFormattedPoints={leadFormattedPoints || undefined}
                      a11yPrice={priceAccessibilityText || ''}
                      lockupPrice={leadFormattedPrice ? leadFormattedPrice : undefined}
                      priceSubtextStandard={leadPriceLabel ? leadPriceLabel : undefined}
                      strikeThroughPrice={strikeOutFormattedPrice ? strikeOutFormattedPrice : undefined}
                    />
                  </UitkLayoutFlexItem>
                  {activity.leadTicket.taxesAndFeesMessage && (
                    <UitkLayoutFlexItem className="all-t-margin-one">
                      <UitkText size={200} align="right" data-testid="activity-taxes-and-fees-message">
                        {activity.leadTicket.taxesAndFeesMessage}
                      </UitkText>
                    </UitkLayoutFlexItem>
                  )}
                  {activity.features.volumePricing?.description && (
                    <UitkLayoutFlexItem className="all-t-margin-one">
                      <UitkText size={100} align="right" data-testid="activity-volume-based-pricing">
                        {activity.features.volumePricing.description}
                      </UitkText>
                    </UitkLayoutFlexItem>
                  )}
                  {activity.features.likelyToSellOut?.formatted && (
                    <UitkLayoutFlexItem className="all-t-margin-one">
                      <UitkText size={200} align="right" theme="negative" data-testid="activity-likely-to-sell-out">
                        {activity.features.likelyToSellOut.formatted}
                      </UitkText>
                    </UitkLayoutFlexItem>
                  )}
                </UitkLayoutFlex>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCardContentSection>
    </UitkLayoutGridItem>
  );
};
